import { Component } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { NewPlanOption } from '../../models/new-plan-option';
import { SAPDataService } from '../../services/sap/sap-data.service';
import { SharedStateService } from '../../services/shared-state/shared-state.service';

@Component({
  selector: 'app-customize-plan',
  templateUrl: './customize-plan.component.' + environment.brand + '.html',
  styleUrls: ['./customize-plan.component.' + environment.brand + '.css'],
  providers: [SAPDataService]
})
export class CustomizePlanComponent {
  planTypeOptions = [];
  selectedPlanType = '';
  yearsOptions = [];
  selectedYears = '';
  distanceOptions = [];
  selectedDistance = '';
  customizedPlan: NewPlanOption;

  constructor(private sharedStateService: SharedStateService, private sapDataService: SAPDataService) {
    //console.log('CustomizePlanComponent.constructor');
    //console.log('VIN', JSON.stringify(this.sharedStateService.selectedVehicleDetail.vin));
  }

  loadPlanTypeOptions(): void {
    // reset cascading data
    this.planTypeOptions = [];
    this.selectedPlanType = '';
    this.yearsOptions = [];
    this.selectedYears = '';
    this.distanceOptions = [];
    this.selectedDistance = '';

    // decide which service call to use
    let getCustomPlanTypes;
    if (!this.sharedStateService.justBrowsing) {
      getCustomPlanTypes = this.sapDataService.getCustomPlanTypes(
        this.sharedStateService.selectedVehicleDetail.vin,
        this.sharedStateService.selectedVehicleDetail.currentOdometer);
    } else {
      getCustomPlanTypes = this.sapDataService.getCustomPlanTypesJustBrowsing(
        this.sharedStateService.selectedVehicleDetail.brandCode,
        this.sharedStateService.selectedVehicleDetail.segment,
        this.sharedStateService.selectedVehicleDetail.currentPlanYears,
        this.sharedStateService.selectedVehicleDetail.currentPlanEndKM,
        +this.sharedStateService.selectedVehicleDetail.year,
        this.sharedStateService.selectedVehicleDetail.currentOdometer,
        this.sharedStateService.selectedVehicleDetail.planType);
    }

    this.sharedStateService.toggleLoader(true);
    getCustomPlanTypes
      .pipe(finalize(() => {
        this.sharedStateService.toggleLoader(false);
      }))
      .subscribe(planTypes => {
        if (planTypes?.length > 0) {
          this.planTypeOptions.push({ value: 'temp', label: 'Please select a plan type' });
          planTypes.forEach(planType => {
            this.planTypeOptions.push({ value: planType, label: planType });
          });

          if (this.sharedStateService.newPlanOption?.isCustomPlan) {
            const planType = this.sharedStateService.newPlanOption.planType;
            if (this.planTypeOptions.findIndex(x => x.value == planType) > -1) {
              this.selectedPlanType = planType;
              this.onPlanTypeSelected('shared_state');
            }
          }

          // set default value
          if (this.selectedPlanType == '') {
            this.selectedPlanType = this.planTypeOptions[0].value;
          }
        }
      }, error => {
        //this.utils.log('loadPlanTypeOptions', error);
        this.sharedStateService.showDialog({
          isVisible: true,
          header: 'Error getting custom plan types',
          message: 'Please try again or contact us for assistance.'
        });
      });
  }

  onPlanTypeSelected(event: any): void {
    //console.log('onPlanTypeSelected [' + this.selectedPlanType + ']', event);

    // reset cascading data
    this.customizedPlan = undefined;
    this.yearsOptions = [];
    this.selectedYears = '';
    this.distanceOptions = [];
    this.selectedDistance = '';

    if (this.selectedPlanType == '' || this.selectedPlanType == 'temp') {
      return;
    }

    if (event != 'shared_state') {
      // invalidate the selected options
      this.sharedStateService.newPlanOption = undefined;
    }

    // decide which service call to use
    let getCustomPlanYears;
    if (!this.sharedStateService.justBrowsing) {
      getCustomPlanYears = this.sapDataService.getCustomPlanYears(
        this.sharedStateService.selectedVehicleDetail.vin,
        this.sharedStateService.selectedVehicleDetail.currentOdometer,
        this.selectedPlanType);
    } else {
      getCustomPlanYears = this.sapDataService.getCustomPlanYearsJustBrowsing(
        this.sharedStateService.selectedVehicleDetail.brandCode,
        this.sharedStateService.selectedVehicleDetail.segment,
        +this.sharedStateService.selectedVehicleDetail.year,
        this.sharedStateService.selectedVehicleDetail.planType,
        this.sharedStateService.selectedVehicleDetail.currentPlanYears,
        this.sharedStateService.selectedVehicleDetail.currentPlanEndKM,
        this.sharedStateService.selectedVehicleDetail.currentOdometer,
        this.selectedPlanType);
    }

    this.sharedStateService.toggleLoader(true);
    getCustomPlanYears
      .pipe(finalize(() => {
        this.sharedStateService.toggleLoader(false);
      }))
      .subscribe(planYears => {
        if (planYears?.length > 0) {
          this.yearsOptions.push({ value: 'temp', label: 'Please select the years' });
          planYears.forEach(year => {
            this.yearsOptions.push({ value: year, label: year });
          });

          if (this.sharedStateService.newPlanOption?.isCustomPlan) {
            const years = this.sharedStateService.newPlanOption.years.toString();
            if (this.yearsOptions.findIndex(x => x.value == years) > -1) {
              this.selectedYears = years;
              this.onYearsSelected();
            }
          }

          if (this.selectedYears == '') {
            this.selectedYears = this.yearsOptions[0].value.toString();
          }
        }
      }, error => {
        //console.log('onPlanTypeSelected', error);
        this.sharedStateService.showDialog({
          isVisible: true,
          header: 'Error getting custom plan years',
          message: 'Please try again or contact us for assistance.'
        });
      });
  }

  onYearsSelected(): void {
    //console.log('onYearsSelected [' + this.selectedYears + ']');
    this.customizedPlan = undefined;
    this.distanceOptions = [];
    this.selectedDistance = '';

    if (this.selectedYears == '' || this.selectedYears == 'temp') {
      return;
    }

    // decide which service call to use
    let getCustomPlanKM;
    if (!this.sharedStateService.justBrowsing) {
      getCustomPlanKM = this.sapDataService.getCustomPlanKM(
        this.sharedStateService.selectedVehicleDetail.vin,
        this.sharedStateService.selectedVehicleDetail.currentOdometer,
        this.selectedPlanType,
        +this.selectedYears);
    } else {
      getCustomPlanKM = this.sapDataService.getCustomPlanKMJustBrowsing(
        this.sharedStateService.selectedVehicleDetail.brandCode,
        this.sharedStateService.selectedVehicleDetail.segment,
        +this.sharedStateService.selectedVehicleDetail.year,
        this.sharedStateService.selectedVehicleDetail.planType,
        this.sharedStateService.selectedVehicleDetail.currentPlanYears,
        this.sharedStateService.selectedVehicleDetail.currentPlanEndKM,
        this.sharedStateService.selectedVehicleDetail.currentOdometer,
        this.selectedPlanType,
        +this.selectedYears);
    }

    this.sharedStateService.toggleLoader(true);
    getCustomPlanKM
      .pipe(finalize(() => {
        this.sharedStateService.toggleLoader(false);
      }))
      .subscribe(planKMs => {
        if (planKMs?.length > 0) {
          this.distanceOptions.push({ value: 'temp', label: 'Please select the distance' });
          planKMs.forEach(year => {
            this.distanceOptions.push({ value: year, label: year });
          });

          if (this.sharedStateService.newPlanOption?.isCustomPlan) {
            const distance = this.sharedStateService.newPlanOption.maxKilometers.toString();
            if (this.distanceOptions.findIndex(x => x.value == distance) > -1) {
              this.selectedDistance = distance;
              this.onDistanceSelected();
            }
          }

          if (this.selectedDistance == '') {
            this.selectedDistance = this.distanceOptions[0].value.toString();
          }
        }
      }, error => {
        //console.log('onYearsSelected', error);
        this.sharedStateService.showDialog({
          isVisible: true,
          header: 'Error getting custom plan distance',
          message: 'Please try again or contact us for assistance.'
        });
      });
  }

  onDistanceSelected(): void {
    //console.log('onDistanceSelected [' + this.selectedDistance + ']');
    this.customizedPlan = undefined;

    if (this.selectedDistance == '' || this.selectedDistance == 'temp') {
      return;
    }

    // decide which service call to use
    let getCustomPlanPrice;
    if (!this.sharedStateService.justBrowsing) {
      getCustomPlanPrice = this.sapDataService.getCustomPlanPrice(
        this.sharedStateService.selectedVehicleDetail.vin,
        this.sharedStateService.selectedVehicleDetail.currentOdometer,
        this.selectedPlanType,
        +this.selectedYears,
        +this.selectedDistance);
    } else {
      getCustomPlanPrice = this.sapDataService.getCustomPlanPriceJustBrowsing(
        this.sharedStateService.selectedVehicleDetail.brandCode,
        this.sharedStateService.selectedVehicleDetail.segment,
        +this.sharedStateService.selectedVehicleDetail.year,
        this.sharedStateService.selectedVehicleDetail.planType,
        this.sharedStateService.selectedVehicleDetail.currentPlanYears,
        this.sharedStateService.selectedVehicleDetail.currentPlanEndKM,
        this.sharedStateService.selectedVehicleDetail.currentOdometer,
        this.selectedPlanType,
        +this.selectedYears,
        +this.selectedDistance);
    }

    this.sharedStateService.toggleLoader(true);
    getCustomPlanPrice
      .pipe(finalize(() => {
        this.sharedStateService.toggleLoader(false);
      }))
      .subscribe(result => {
        //console.log('getCustomPlanPrice', JSON.stringify(result));
        this.customizedPlan = new NewPlanOption();
        this.customizedPlan.isCustomPlan = true;
        this.customizedPlan.price = result.price;
        this.customizedPlan.planType = this.selectedPlanType;
        this.customizedPlan.salesDocType = result.salesDocType;
        this.customizedPlan.years = +this.selectedYears;
        this.customizedPlan.planStartDate = result.planStartDate;
        this.customizedPlan.planEndDate = result.planEndDate;
        this.customizedPlan.planStartKM = result.planStartKM;
        this.customizedPlan.maxKilometers = result.planEndKM;
        //console.log('customizedPlan', JSON.stringify(this.customizedPlan));
      }, error => {
        //console.log('onDistanceSelected', error);
        this.sharedStateService.showDialog({
          isVisible: true,
          header: 'Error getting custom plan price',
          message: 'Please try again or contact us for assistance.'
        });
      });
  }
}
